// Location parsing utilities for consistent city/state/zip extraction

export interface ParsedLocation {
  city: string;
  state: string;
  stateAbbreviation: string;
  zipCode: string;
  originalLocation: string;
  confidence: number;
  parsed: boolean;
}

// US State mappings
const STATE_ABBREVIATIONS: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district of columbia': 'DC', 'puerto rico': 'PR'
};

const STATE_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([name, abbr]) => [abbr, name])
);

/**
 * Parse location string into structured components
 * Handles various formats:
 * - "Houston, TX"
 * - "Houston, Texas"
 * - "Houston, TX 77001"
 * - "Houston, Texas 77001"
 * - "123 Main St, Houston, TX"
 * - "123 Main St, Houston, TX 77001"
 */
export function parseLocation(location: string): ParsedLocation {
  const result: ParsedLocation = {
    city: '',
    state: '',
    stateAbbreviation: '',
    zipCode: '',
    originalLocation: location,
    confidence: 0,
    parsed: false
  };

  if (!location || typeof location !== 'string') {
    return result;
  }

  const trimmed = location.trim();
  if (!trimmed) {
    return result;
  }

  // Split by commas and clean each part
  const parts = trimmed.split(',').map(part => part.trim()).filter(Boolean);
  
  if (parts.length === 0) {
    return result;
  }

  // Extract ZIP code from the last part (5 digits, optionally followed by -4 digits)
  let zipCode = '';
  let lastPart = parts[parts.length - 1];
  const zipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
  if (zipMatch) {
    zipCode = zipMatch[1];
    lastPart = lastPart.replace(zipMatch[0], '').trim();
    result.confidence += 0.3;
  }

  // Now we have cleaned parts, try different parsing strategies
  let city = '';
  let state = '';
  let stateAbbr = '';

  if (parts.length >= 2) {
    // Strategy 1: Last part (after ZIP removal) is state
    const statePart = lastPart || parts[parts.length - 1];
    const stateInfo = parseState(statePart);
    
    if (stateInfo.found) {
      state = stateInfo.fullName;
      stateAbbr = stateInfo.abbreviation;
      result.confidence += 0.4;
      
      // City is the second-to-last part (or first if only 2 parts)
      if (parts.length === 2) {
        city = parts[0];
      } else {
        // Multiple parts - take the one before the state part
        const cityIndex = parts.length - 2;
        city = parts[cityIndex];
      }
      result.confidence += 0.3;
    }
  }

  // Fallback: If we couldn't parse state, try other strategies
  if (!state && parts.length >= 1) {
    // Try to find state in any part
    for (let i = 0; i < parts.length; i++) {
      const stateInfo = parseState(parts[i]);
      if (stateInfo.found) {
        state = stateInfo.fullName;
        stateAbbr = stateInfo.abbreviation;
        result.confidence += 0.2;
        
        // Take the part before the state as city
        if (i > 0) {
          city = parts[i - 1];
          result.confidence += 0.2;
        }
        break;
      }
    }
  }

  // If still no city and we have at least one part, use the first non-address part
  if (!city && parts.length > 0) {
    // Skip obvious address parts (numbers at the start)
    for (const part of parts) {
      if (!part.match(/^\d/)) {
        city = part;
        result.confidence += 0.1;
        break;
      }
    }
    
    // If all parts start with numbers, take the first one anyway
    if (!city) {
      city = parts[0];
      result.confidence += 0.05;
    }
  }

  // Clean up results
  result.city = cleanLocationPart(city);
  result.state = state;
  result.stateAbbreviation = stateAbbr;
  result.zipCode = zipCode;
  result.parsed = !!(result.city || result.state);

  return result;
}

/**
 * Parse a potential state string (full name or abbreviation)
 */
function parseState(statePart: string): { found: boolean; fullName: string; abbreviation: string } {
  if (!statePart) {
    return { found: false, fullName: '', abbreviation: '' };
  }

  const cleaned = statePart.toLowerCase().trim();
  
  // Check if it's a state abbreviation (2 letters)
  if (cleaned.length === 2) {
    const upperCase = cleaned.toUpperCase();
    const fullName = STATE_NAMES[upperCase];
    if (fullName) {
      return { found: true, fullName, abbreviation: upperCase };
    }
  }
  
  // Check if it's a full state name
  const abbreviation = STATE_ABBREVIATIONS[cleaned];
  if (abbreviation) {
    return { found: true, fullName: cleaned, abbreviation };
  }
  
  return { found: false, fullName: '', abbreviation: '' };
}

/**
 * Clean a location part (city or other component)
 */
function cleanLocationPart(part: string): string {
  if (!part) return '';
  
  return part
    .trim()
    // Remove common prefixes that might be part of an address
    .replace(/^(avenue|ave|street|st|road|rd|drive|dr|lane|ln|way|court|ct|circle|cir|boulevard|blvd|parkway|pkwy|place|pl)\s+/i, '')
    // Clean up common suffixes
    .replace(/\s+(avenue|ave|street|st|road|rd|drive|dr|lane|ln|way|court|ct|circle|cir|boulevard|blvd|parkway|pkwy|place|pl)$/i, '')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract city and state for URL generation (backward compatibility)
 */
export function extractCityState(location: string): { city: string; state: string } {
  const parsed = parseLocation(location);
  return {
    city: parsed.city,
    state: parsed.stateAbbreviation || parsed.state
  };
}

/**
 * Format location consistently for display
 */
export function formatLocation(parsed: ParsedLocation): string {
  const parts: string[] = [];
  
  if (parsed.city) {
    parts.push(parsed.city);
  }
  
  if (parsed.stateAbbreviation) {
    parts.push(parsed.stateAbbreviation);
  } else if (parsed.state) {
    parts.push(parsed.state);
  }
  
  if (parsed.zipCode) {
    parts.push(parsed.zipCode);
  }
  
  return parts.join(', ');
}

/**
 * Validate if a location string has sufficient information for SEO footer
 */
export function isValidLocationForSeo(location: string): boolean {
  const parsed = parseLocation(location);
  return parsed.parsed && !!parsed.city && !!parsed.stateAbbreviation && parsed.confidence > 0.3;
}

/**
 * Get SEO-friendly location components
 */
export function getSeoLocationComponents(location: string): {
  city: string;
  state: string;
  stateAbbreviation: string;
  zipCode: string;
  isValid: boolean;
} {
  const parsed = parseLocation(location);
  return {
    city: parsed.city,
    state: parsed.state,
    stateAbbreviation: parsed.stateAbbreviation,
    zipCode: parsed.zipCode,
    isValid: isValidLocationForSeo(location)
  };
}

/**
 * Suggest better location format
 */
export function suggestLocationFormat(location: string): string {
  const parsed = parseLocation(location);
  
  if (!parsed.parsed) {
    return location; // Return original if we couldn't parse it
  }
  
  // Prefer "City, ST" format for consistency
  if (parsed.city && parsed.stateAbbreviation) {
    let formatted = `${parsed.city}, ${parsed.stateAbbreviation}`;
    if (parsed.zipCode) {
      formatted += ` ${parsed.zipCode}`;
    }
    return formatted;
  }
  
  return formatLocation(parsed);
} 